/**
 * Lecturas de «Tu día» (Req. 24). Solo lee: apta para useLiveQuery.
 *
 * Las tablas de comidas y entrenamientos existen desde el esquema 1. Las
 * comidas se registran desde la Fase 1; los entrenamientos, desde la Fase 2
 * (mientras tanto solo llegan al restaurar un respaldo).
 */

import { db, leerSettings } from './schema';
import { proximaSesion, type EntrenamientoConEjercicios, type ProximaSesion } from '@/domain/hoy';
import type { FechaLocal } from '@/domain/dates';
import { modalidadDe } from '@/domain/modalidad';
import { propuestasDeGym } from '@/domain/sesiones';
import { CICLOS } from '@/datos/rutinas-precargadas';
import type { MealEntry, Routine, Settings, Workout } from './types';

export interface DatosDeHoy {
  comidas: MealEntry[];
  enCurso: EntrenamientoConEjercicios | null;
  ultima: EntrenamientoConEjercicios | null;
  /** La que propone Gym en la modalidad que muestra (tarea 14); null si no propone ninguna. */
  proxima: ProximaSesion | null;
}

/** Lo mismo que propone Gym en la modalidad que muestra (`propuestasDeGym`), para Hoy. */
function proximaDe(
  ajustes: Pick<Settings, 'modalidadGym'>,
  rutinas: readonly Routine[],
  terminados: readonly Workout[],
  conSeries: ReadonlySet<string>,
  fecha: FechaLocal,
): ProximaSesion | null {
  const modalidad = modalidadDe({ modalidad: ajustes.modalidadGym });
  const { propuestas, siguientes } = propuestasDeGym(rutinas, terminados, conSeries, CICLOS, modalidad, fecha);
  return proximaSesion(propuestas, siguientes, modalidad);
}

async function conEjercicios(w: Workout | undefined): Promise<EntrenamientoConEjercicios | null> {
  if (w === undefined) return null;
  const ejercicios = await db.workoutExercises.where('workoutId').equals(w.id).count();
  return { entrenamiento: w, ejercicios };
}

export async function leerDatosDeHoy(fecha: FechaLocal): Promise<DatosDeHoy> {
  const [comidas, abierto, terminados, ids, rutinas, ajustes] = await Promise.all([
    db.mealEntries.where('fecha').equals(fecha).toArray(),
    db.workouts.where('estado').equals('en_curso').first(),
    db.workouts.where('estado').equals('terminado').toArray(),
    db.workoutSets.orderBy('workoutId').uniqueKeys(),
    db.routines.toArray(),
    leerSettings(),
  ]);
  const conSeries = new Set(ids.map(String));
  // El último terminado con al menos una serie, por el instante en que empezó:
  // uno vacío no cuenta como entrenado (0.3.7), igual que en Gym
  const ultimo = terminados
    .filter((w) => conSeries.has(w.id))
    .sort((a, b) => b.inicio.localeCompare(a.inicio))[0];
  return {
    comidas,
    enCurso: await conEjercicios(abierto),
    ultima: await conEjercicios(ultimo),
    proxima: proximaDe(ajustes, rutinas, terminados, conSeries, fecha),
  };
}

export interface RutinaResumida {
  id: string;
  nombre: string;
  ejercicios: number;
}

/** Rutinas guardadas, en su orden, con cuántos ejercicios tiene cada una. */
export async function leerRutinas(): Promise<RutinaResumida[]> {
  const rutinas = await db.routines.orderBy('orden').toArray();
  return Promise.all(
    rutinas.map(async (r) => ({
      id: r.id,
      nombre: r.nombre,
      ejercicios: await db.routineExercises.where('routineId').equals(r.id).count(),
    })),
  );
}
